import React from 'react'
import { useFormContext } from 'react-hook-form'
import { Text, View } from 'react-native'

import { useTranslation } from '@flows-ui/utils/i18n'
import type {
  AnyStepContentElement,
  InjectedStepContentRendererProps,
} from '@flows/core/types/stepContentTypes'
import type { ProductKey } from '@flows/insurance/types/productKeys'
import { getProductParamPath } from '@flows/insurance/utils'
import { RadioSet } from '@ui-kit/components/formComponents/RadioSet'
import Select from '@ui-kit/components/formComponents/Select'
import Spacer from '@ui-kit/components/layoutComponents/Spacer'
import { body2 } from '@ui-kit/core/typography'

const CLAIMS_OPTIONS = [
  { label: '0', value: 0 },
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3+', value: 3 },
]

export type InstantQuoteQuestionsProps = InjectedStepContentRendererProps<AnyStepContentElement> & {
  productKey: ProductKey
  damagesTitleKey: string
  damagesLabelKey?: string
}

export const InstantQuoteQuestions = ({
  productKey,
  damagesTitleKey,
  damagesLabelKey = 'sharedFlows_common:customComponents.liabilityInstantQuoteQuestions.damages.label',
  ...props
}: InstantQuoteQuestionsProps) => {
  const { watch, setValue, handleSubmit } = useFormContext()
  const { t } = useTranslation()

  const previousInsurancePath = getProductParamPath(productKey, 'previous_insurance_in_last_5y')
  const claimsPath = getProductParamPath(productKey, 'number_of_claims_in_last_5y')

  const hadPreviousInsurance = watch(previousInsurancePath)

  return (
    <View>
      <RadioSet
        name={previousInsurancePath}
        id={previousInsurancePath}
        radioSetType="radioChip"
        radioOptions={[
          { label: t('sharedFlows_common:generic.inputs.yes'), value: true },
          { label: t('sharedFlows_common:generic.inputs.no'), value: false },
        ]}
        value={hadPreviousInsurance}
        onValueChange={(value) => {
          setValue(previousInsurancePath, value, { shouldValidate: true })
        }}
      />
      {hadPreviousInsurance === true && (
        <>
          <Spacer size="small" />
          <Text className={body2({ color: 'grey700' })}>{t(damagesTitleKey)}</Text>
          <Spacer size="xsmall" />
          <Select
            optionId="dropdown_previous_claims"
            testID="dropdown_previous_claims"
            label={t(damagesLabelKey)}
            styleType="outlined"
            name={claimsPath}
            value={watch(claimsPath)}
            options={CLAIMS_OPTIONS}
            useFilterInput={false}
            onValueChange={async (value) => {
              setValue(claimsPath, value, { shouldValidate: true })
              if (value !== undefined && value !== null) {
                await handleSubmit(props.submitHandler)()
              }
            }}
          />
        </>
      )}
    </View>
  )
}
